import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  IconButton,
  Button,
  makeStyles,
  CircularProgress,
  Typography,
} from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import CheckIcon from "@material-ui/icons/Check";
import CloseIcon from "@material-ui/icons/Close";
import axios from "axios";
import { appUrl } from "../../../../../utils/config";

/*
  BCM-881 — the Reconciliation Run Per CMO tab in Sponsor Network Configuration.

  It holds the schedule the reconciliation job runs on for each CMO. A CMO is
  listed once a row has been added for it through Add New Row, which offers
  the active CMOs without a row yet. Each value is edited in place through the
  pencil beside it.

  Custom Report Date Fallback carries a pencil like the rest, but Immediate
  Future is offered greyed out: the product team asked for the field to be
  editable while only Immediate Past is actually supported. The middleware
  forces Immediate Past regardless, so the two cannot disagree.

  The CMO list comes from fetchCMOMembersDirect; the schedules from
  fetchReconciliationJobRunConfigurations, saved through
  addReconciliationJobRunConfigurations (new rows) and
  updateReconciliationJobRunConfigurations (saved rows). The middleware always
  stores Custom Report Date Fallback as Immediate Past.
*/

const BASE = () =>
  `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}`;

// These endpoints reject with HTTP 200 and status false, so the rejection has
// to be read off the body; only an explicit false counts as one.
const rejectionOf = (response, fallback) => {
  const data = response?.data;
  if (!data || data.status !== false) return "";
  return data.errorMessage || data.errorDescription || data.message || fallback;
};

const TRIGGER_POINTS = ["Day Wise", "Month Wise"];
const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const DAYS_OF_MONTH = Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, "0"));
// The API carries the day of the month as a number; the dropdown's options are
// zero padded, so a saved 1 has to become "01" or nothing is selected.
const toDayOption = (value) =>
  value === null || value === undefined || value === "" ? "" : String(value).padStart(2, "0");
const TIME_ZONES = ["UTC", "IST", "EST", "CST", "PST", "CET", "GMT"];
const INVENTORY_FROM = ["Immediate Past", "Immediate Future"];

// The FSD's default for a CMO that carries no saved schedule.
const DEFAULT_ROW = {
  triggerPoint: "Day Wise",
  selectDaysMonths: ["Monday"],
  dayOfTheMonth: "",
  time: "02:00",
  timeZone: "UTC",
  useCmoInventoryFrom: "Immediate Past",
  customReportDateFallback: "Immediate Past",
};

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    padding: theme.spacing(2),
  },
  table: {
    backgroundColor: "#fff",
    border: "1px solid #ddd",
    minWidth: "1000px",
  },
  // Headings wrap onto a second line only when the screen is too narrow for
  // the table, so it fits without a horizontal scrollbar.
  headerCell: {
    fontWeight: 600,
    color: "#333",
    backgroundColor: "#f0f0f0",
    padding: "10px 14px",
    textAlign: "left",
    whiteSpace: "normal",
  },
  bodyCell: {
    padding: "8px 14px",
    textAlign: "left",
    verticalAlign: "middle",
    whiteSpace: "nowrap",
  },
  valueCell: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    justifyContent: "space-between",
  },
  // Select Days/Months: a long list of days or months wraps within at most
  // 220px rather than stretching the table, and narrows further on a small
  // screen.
  daysMonthsCell: {
    width: "220px",
    minWidth: "160px",
    maxWidth: "220px",
    whiteSpace: "normal",
    wordBreak: "break-word",
  },
  // While editing, the picker stays inside that width; MUI cuts its text
  // short with an ellipsis.
  daysMonthsEditor: {
    maxWidth: "140px",
  },
  // Use CMO Inventory From and Custom Report Date Fallback: the heading's last
  // words wrap to a second line, so the table fits the screen without a
  // horizontal scrollbar.
  wrapCell: {
    width: "140px",
    minWidth: "110px",
    maxWidth: "160px",
    whiteSpace: "normal",
  },
  // A long CMO name wraps rather than widening the table.
  cmoNameCell: {
    minWidth: "140px",
    whiteSpace: "normal",
  },
  pencil: {
    color: "#ec6535",
    padding: "2px",
  },
  // Custom Report Date Fallback, and Day of the Month on a Day Wise row, carry
  // no pencil; this keeps the column's width steady so the table does not jump.
  pencilPlaceholder: {
    width: "28px",
    display: "inline-block",
  },
  addButton: {
    marginTop: 16,
    backgroundColor: "#fff",
    color: "#000",
    borderRadius: "20px",
    padding: "6px 16px",
    textTransform: "none",
    fontWeight: 600,
    border: "1px solid #000",
    display: "flex",
    alignItems: "center",
    "&:hover": { backgroundColor: "#f5f5f5" },
  },
  addIconCircle: {
    width: "24px",
    height: "24px",
    borderRadius: "50%",
    border: "2px solid #000",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 8,
    fontSize: "16px",
    fontWeight: 600,
  },
  editor: {
    minWidth: "130px",
    "& input": { padding: "6px 8px", height: "28px" },
  },
  saveButton: {
    backgroundColor: "#ec6535",
    color: "#fff",
    borderRadius: "20px",
    padding: "8px 24px",
    textTransform: "none",
    fontWeight: 600,
    whiteSpace: "nowrap",
    "&:hover": { backgroundColor: "#d4552a" },
  },
  resetChangesButton: {
    border: "1px solid #000000",
    color: "#000000",
    fontWeight: 600,
    borderRadius: "20px",
    padding: "6px 16px",
    textTransform: "none",
    whiteSpace: "nowrap",
  },
  notApplicable: { color: "#bdbdbd" },
}));

const ReconciliationRunPerCmoTab = (props) => {
  const classes = useStyles();
  const { featuresAccess, sponsorId, onRowsFetched } = props;
  const canEdit = featuresAccess?.editSponsorNetworkDetails ?? false;

  const [rows, setRows] = useState([]);
  // Every active CMO, so Add New Row can offer the ones with no row yet.
  const [allCmos, setAllCmos] = useState([]);
  // Rows added in this session, which carry a CMO picker until one is chosen.
  const [newRowKeys, setNewRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isUpdated, setIsUpdated] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  // { "<rowIndex>:<field>": true } — which cell is currently being edited.
  const [editing, setEditing] = useState({});

  const loadRows = async () => {
    setLoading(true);
    try {
      const [cmoResponse, configResponse] = await Promise.all([
        axios.post(`${BASE()}/fetchCMOMembersDirect`, {}).catch((err) => {
          console.error("fetchCMOMembersDirect:", err);
          return { data: {} };
        }),
        axios
          .post(`${BASE()}/fetchReconciliationJobRunConfigurations`, { sponsorId })
          .catch((err) => {
            // Not delivered yet — every CMO then shows the FSD's default.
            console.error("fetchReconciliationJobRunConfigurations:", err);
            return { data: {} };
          }),
      ]);

      const cmos = cmoResponse?.data?.CMOMembersWithId || [];
      const saved =
        configResponse?.data?.reconciliationJobRunConfigurations ||
        configResponse?.data?.data?.reconciliationJobRunConfigurations ||
        [];
      /*
        The configurations come back keyed on vendorMosymphonyID, while the CMO
        list is keyed on organizationId. They are the same identifier under two
        names, and one side may hand it over as a number and the other as a
        string, so the map is keyed on its string form.
      */
      const keyOf = (value) =>
        value === null || value === undefined ? "" : String(value);
      const savedBy = new Map(
        saved
          .filter((item) => item)
          .map((item) => [
            keyOf(item.vendorMosymphonyID ?? item.organizationId),
            item,
          ])
      );

      setAllCmos(cmos);

      /*
        A row exists for a CMO only once a reconciliation run has been
        configured for it: "A CMO is not listed in the tab until a row has been
        added for it." The list is therefore built from the saved
        configurations, with the CMO's name looked up for display, rather than
        from every CMO in the network.
      */
      // The configuration carries its own vendorName, which stands in when the
      // CMO list has not been read or no longer holds that CMO, so a saved row
      // never shows a blank name.
      const nameOf = (vendorId, vendorName) => {
        const cmo = cmos.find((c) => keyOf(c.organizationId) === keyOf(vendorId));
        return (cmo && cmo.organizationName) || vendorName || "";
      };

      const built = saved.filter((item) => item).map((config) => {
        const match = config;
        const organizationId = match.vendorMosymphonyID ?? match.organizationId;
        const monthWise = (match.triggerPoint || DEFAULT_ROW.triggerPoint) === "Month Wise";
        // The API carries the selection as a comma separated string in
        // dayName or monthName depending on the trigger; the tab holds one
        // array and splits it back out on save.
        const selected = (monthWise ? match.monthName : match.dayName) || "";
        return {
          ...DEFAULT_ROW,
          ...match,
          organizationId,
          cmoName: nameOf(organizationId, match.vendorName),
          reconciliationJobRunID: match.reconciliationJobRunID ?? null,
          dayOfTheMonth: toDayOption(
            match.dayOfTheMonth ?? (monthWise ? DEFAULT_ROW.dayOfTheMonth : "")
          ),
          useCmoInventoryFrom:
            match.useCMOInventoryFrom || match.useCmoInventoryFrom || DEFAULT_ROW.useCmoInventoryFrom,
          customReportDateFallback:
            match.customReportDataFallback ||
            match.customReportDateFallback ||
            DEFAULT_ROW.customReportDateFallback,
          selectDaysMonths: selected
            ? selected.split(",").map((part) => part.trim()).filter(Boolean)
            : monthWise
              ? []
              : DEFAULT_ROW.selectDaysMonths,
        };
      });

      setRows(built);
      if (onRowsFetched) onRowsFetched(built);
    } catch (err) {
      console.error("ReconciliationRunPerCmoTab:", err);
      setRows([]);
      if (onRowsFetched) onRowsFetched([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const editKey = (index, field) => `${index}:${field}`;
  const isEditing = (index, field) => !!editing[editKey(index, field)];
  const openEditor = (index, field) =>
    setEditing((prev) => ({ ...prev, [editKey(index, field)]: true }));
  const closeEditor = (index, field) =>
    setEditing((prev) => {
      const next = { ...prev };
      delete next[editKey(index, field)];
      return next;
    });

  const setValue = (index, field, value) => {
    setRows((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value, isUpdated: true };
      // Switching the trigger point changes what the next two columns mean,
      // so reset them rather than carrying days over into months.
      if (field === "triggerPoint") {
        next[index].selectDaysMonths = [];
        next[index].dayOfTheMonth = value === "Month Wise" ? "01" : "";
      }
      return next;
    });
    setIsUpdated(true);
  };

  /* ---- one editable cell ------------------------------------------- */

  const renderEditableCell = (index, field, display, editor) => (
    <div className={classes.valueCell}>
      {isEditing(index, field) ? (
        <React.Fragment>
          {editor}
          <span style={{ display: "inline-flex" }}>
            <IconButton size="small" onClick={() => closeEditor(index, field)}>
              <CheckIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => closeEditor(index, field)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </span>
        </React.Fragment>
      ) : (
        <React.Fragment>
          <span>{display}</span>
          {canEdit && (
            <IconButton
              size="small"
              className={classes.pencil}
              onClick={() => openEditor(index, field)}
              aria-label={`edit ${field}`}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          )}
        </React.Fragment>
      )}
    </div>
  );

  // disabledOptions: shown in the list but not selectable. Custom Report Date
  // Fallback uses it to offer Immediate Future greyed out until it is supported.
  const selectEditor = (index, field, options, disabledOptions = []) => (
    <Select
      value={rows[index][field] || ""}
      onChange={(e) => setValue(index, field, e.target.value)}
      className={classes.editor}
      displayEmpty
    >
      <MenuItem value="">
        <span style={{ color: "#9e9e9e" }}>Select</span>
      </MenuItem>
      {options.map((option) => (
        <MenuItem
          key={option}
          value={option}
          disabled={disabledOptions.includes(option)}
        >
          {option}
        </MenuItem>
      ))}
    </Select>
  );

  const multiSelectEditor = (index, options) => {
    const value = rows[index].selectDaysMonths || [];
    const allSelected = options.length > 0 && value.length === options.length;
    return (
      <Select
        multiple
        className={`${classes.editor} ${classes.daysMonthsEditor}`}
        value={value}
        onChange={(e) => {
          const picked = e.target.value;
          // "All" toggles the whole list rather than being stored as a value.
          if (picked.includes("__all__")) {
            setValue(index, "selectDaysMonths", allSelected ? [] : [...options]);
            return;
          }
          setValue(index, "selectDaysMonths", picked);
        }}
        renderValue={(selected) =>
          selected.length === 0
            ? "Select"
            : selected.length === options.length
              ? "All"
              : selected.join(", ")
        }
        displayEmpty
      >
        <MenuItem value="__all__">
          <Checkbox size="small" checked={allSelected} />
          <ListItemText primary="All" />
        </MenuItem>
        {options.map((option) => (
          <MenuItem key={option} value={option}>
            <Checkbox size="small" checked={value.indexOf(option) > -1} />
            <ListItemText primary={option} />
          </MenuItem>
        ))}
      </Select>
    );
  };

  /* ---- save / reset ------------------------------------------------ */

  const handleSaveChanges = async () => {
    const changed = rows.filter((row) => row.isUpdated);
    if (!changed.length) return;
    try {
      /*
        A CMO with no saved schedule is an add; one that already has a
        reconciliationJobRunID is an update. Middleware rejects an add for a
        CMO that already holds a config, so the two cannot be merged.

        Day Wise sends dayName and nulls monthName and dayOfTheMonth; Month
        Wise does the reverse. customReportDataFallback is spelled the way the
        API spells it, and carries whatever the row holds rather than a literal,
        now that the cell is editable -- in practice always Immediate Past,
        since Immediate Future is greyed out and the middleware forces it too.
      */
      const toPayload = (row) => {
        const monthWise = row.triggerPoint === "Month Wise";
        const selection = (row.selectDaysMonths || []).join(",");
        return {
          reconciliationJobRunType: "Inventory",
          vendorMosymphonyID: row.organizationId,
          triggerPoint: row.triggerPoint,
          monthName: monthWise ? selection || null : null,
          dayName: monthWise ? null : selection || null,
          // Back to the number the API documents, from the padded option value.
          dayOfTheMonth: monthWise && row.dayOfTheMonth ? Number(row.dayOfTheMonth) : null,
          time: row.time,
          timeZone: row.timeZone,
          useCMOInventoryFrom: row.useCmoInventoryFrom,
          customReportDataFallback:
            row.customReportDateFallback || "Immediate Past",
        };
      };

      const toAdd = changed.filter((row) => !row.reconciliationJobRunID);
      const toUpdate = changed.filter((row) => row.reconciliationJobRunID);

      if (toAdd.length) {
        const response = await axios.post(`${BASE()}/addReconciliationJobRunConfigurations`, {
          sponsorId,
          reconciliationJobRunConfigurations: toAdd.map(toPayload),
        });
        const rejected = rejectionOf(
          response,
          "Failed to add the reconciliation run configuration."
        );
        if (rejected) {
          // isUpdated stays true, so the edits are still on screen to correct.
          setErrorMessage(rejected);
          return;
        }
      }
      if (toUpdate.length) {
        const response = await axios.post(`${BASE()}/updateReconciliationJobRunConfigurations`, {
          sponsorId,
          reconciliationJobRunConfigurations: toUpdate.map((row) => ({
            reconciliationJobRunID: row.reconciliationJobRunID,
            ...toPayload(row),
          })),
        });
        const rejected = rejectionOf(
          response,
          "Failed to update the reconciliation run configuration."
        );
        if (rejected) {
          setErrorMessage(rejected);
          return;
        }
      }
      setIsUpdated(false);
      setErrorMessage("");
      await loadRows();
    } catch (err) {
      console.error("reconciliationJobRunConfigurations save:", err);
      setErrorMessage(
        "Failed to update the reconciliation run configuration. Please try again."
      );
    }
  };

  /*
    "A reconciliation run is configured for a CMO by adding a row through Add
    New Row … the CMO Name dropdown is populated with the active CMOs that do
    not already have a row in the tab."
  */
  const takenCmoIds = new Set(rows.map((row) => String(row.organizationId ?? "")));
  const availableCmos = allCmos.filter(
    (cmo) => !takenCmoIds.has(String(cmo.organizationId))
  );

  const handleAddRow = () => {
    const key = `new-${Date.now()}`;
    setNewRowKeys((prev) => [...prev, key]);
    setRows((prev) => [
      ...prev,
      // The defaults the FSD lists for a new row.
      { ...DEFAULT_ROW, rowKey: key, organizationId: "", cmoName: "", reconciliationJobRunID: null },
    ]);
    setIsUpdated(true);
  };

  /*
    A row added through Add New Row exists only on screen until it is saved, so
    it carries its own control to drop it again. Editing state is keyed on the
    row index, so it is cleared rather than left pointing at a shifted row.
  */
  const handleRemoveNewRow = (index) => {
    const row = rows[index];
    if (!row || row.reconciliationJobRunID) return;
    const remaining = rows.filter((_, i) => i !== index);
    setRows(remaining);
    setNewRowKeys((prev) => prev.filter((key) => key !== row.rowKey));
    setEditing({});
    setErrorMessage("");
    setIsUpdated(
      remaining.some((item) => item.isUpdated || !item.reconciliationJobRunID)
    );
  };

  const handleCmoPick = (index, organizationId) => {
    const cmo = allCmos.find((c) => String(c.organizationId) === String(organizationId));
    setRows((prev) =>
      prev.map((row, i) =>
        i === index
          ? { ...row, organizationId, cmoName: cmo ? cmo.organizationName : "", isUpdated: true }
          : row
      )
    );
    setIsUpdated(true);
  };

  const handleResetChanges = () => {
    if (!isUpdated) return;
    setEditing({});
    loadRows();
    setIsUpdated(false);
    setErrorMessage("");
  };

  /* ---- render ------------------------------------------------------ */

  return (
    <div
      className={classes.container}
      style={{ display: "flex", flexDirection: "column", gap: "24px" }}
    >
      <div style={{ display: "flex", gap: "40px", alignItems: "flex-start", width: "100%" }}>
        {/* Shrinks to the room left beside the Save / Reset buttons, so the page
            itself never scrolls sideways; below the table's minimum width the
            table scrolls inside this box instead. */}
        <div style={{ overflowX: "auto", flex: "1 1 auto", minWidth: 0 }}>
          <Table className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell className={classes.headerCell}>CMO Name</TableCell>
                <TableCell className={classes.headerCell}>Trigger Point</TableCell>
                <TableCell className={`${classes.headerCell} ${classes.daysMonthsCell}`}>
                  Select Days/Months
                </TableCell>
                <TableCell className={classes.headerCell}>Day of the Month</TableCell>
                <TableCell className={classes.headerCell}>Time</TableCell>
                <TableCell className={classes.headerCell}>Time Zone</TableCell>
                <TableCell className={`${classes.headerCell} ${classes.wrapCell}`}>
                  Use CMO Inventory From
                </TableCell>
                <TableCell className={`${classes.headerCell} ${classes.wrapCell}`}>
                  Custom Report Date Fallback
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "100px",
                      }}
                    >
                      <CircularProgress />
                    </div>
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" style={{ padding: "32px" }}>
                    <Typography variant="body2" color="textSecondary">
                      No reconciliation run configured yet. Use Add New Row to
                      configure one for a CMO.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row, index) => {
                  const monthWise = row.triggerPoint === "Month Wise";
                  return (
                    <TableRow key={row.rowKey || row.organizationId || index}>
                      <TableCell className={`${classes.bodyCell} ${classes.cmoNameCell}`}>
                        <div style={{ display: "flex", alignItems: "center" }}>
                          {row.organizationId ? (
                            row.cmoName
                          ) : (
                            <Select
                              className={classes.editor}
                              value=""
                              displayEmpty
                              onChange={(e) => handleCmoPick(index, e.target.value)}
                              disabled={!canEdit}
                            >
                              <MenuItem value="" disabled>
                                Select
                              </MenuItem>
                              {availableCmos.map((cmo) => (
                                <MenuItem key={cmo.organizationId} value={cmo.organizationId}>
                                  {cmo.organizationName}
                                </MenuItem>
                              ))}
                            </Select>
                          )}
                          {canEdit && !row.reconciliationJobRunID && (
                            <IconButton
                              aria-label="Remove row"
                              title="Remove row"
                              className={classes.pencil}
                              onClick={() => handleRemoveNewRow(index)}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          )}
                        </div>
                      </TableCell>

                      <TableCell className={classes.bodyCell}>
                        {renderEditableCell(
                          index,
                          "triggerPoint",
                          row.triggerPoint,
                          selectEditor(index, "triggerPoint", TRIGGER_POINTS)
                        )}
                      </TableCell>

                      <TableCell className={`${classes.bodyCell} ${classes.daysMonthsCell}`}>
                        {renderEditableCell(
                          index,
                          "selectDaysMonths",
                          (row.selectDaysMonths || []).length === 0
                            ? ""
                            : (row.selectDaysMonths || []).length ===
                              (monthWise ? MONTHS : DAYS).length
                              ? monthWise ? "All Months" : "All Days"
                              : (row.selectDaysMonths || []).join(", "),
                          multiSelectEditor(index, monthWise ? MONTHS : DAYS)
                        )}
                      </TableCell>

                      <TableCell className={classes.bodyCell}>
                        {/* Day of the Month applies only where Trigger Point is
                            Month Wise; it is NA for a Day Wise run. */}
                        {monthWise ? (
                          renderEditableCell(
                            index,
                            "dayOfTheMonth",
                            row.dayOfTheMonth,
                            selectEditor(index, "dayOfTheMonth", DAYS_OF_MONTH)
                          )
                        ) : (
                          <div className={classes.valueCell}>
                            <span className={classes.notApplicable}>NA</span>
                            <span className={classes.pencilPlaceholder} />
                          </div>
                        )}
                      </TableCell>

                      <TableCell className={classes.bodyCell}>
                        {renderEditableCell(
                          index,
                          "time",
                          row.time,
                          <TextField
                            type="time"
                            value={row.time || ""}
                            onChange={(e) => setValue(index, "time", e.target.value)}
                            className={classes.editor}
                            variant="outlined"
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ step: 300 }}
                          />
                        )}
                      </TableCell>

                      <TableCell className={classes.bodyCell}>
                        {renderEditableCell(
                          index,
                          "timeZone",
                          row.timeZone,
                          selectEditor(index, "timeZone", TIME_ZONES)
                        )}
                      </TableCell>

                      <TableCell className={`${classes.bodyCell} ${classes.wrapCell}`}>
                        {renderEditableCell(
                          index,
                          "useCmoInventoryFrom",
                          row.useCmoInventoryFrom,
                          selectEditor(index, "useCmoInventoryFrom", INVENTORY_FROM)
                        )}
                      </TableCell>

                      <TableCell className={`${classes.bodyCell} ${classes.wrapCell}`}>
                        {/* Editable, but Immediate Future is offered greyed out
                            until the reconciliation supports falling forward —
                            confirmed with the product team. */}
                        {renderEditableCell(
                          index,
                          "customReportDateFallback",
                          row.customReportDateFallback || "Immediate Past",
                          selectEditor(
                            index,
                            "customReportDateFallback",
                            INVENTORY_FROM,
                            ["Immediate Future"]
                          )
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>

          {/* A run is configured for a CMO by adding a row here; the dropdown
              offers the CMOs that do not already have one. */}
          {canEdit && (
            <Button
              variant="outlined"
              onClick={handleAddRow}
              className={classes.addButton}
              disabled={availableCmos.length === 0}
              title={
                availableCmos.length === 0
                  ? "Every CMO already has a reconciliation run configured."
                  : ""
              }
            >
              <span className={classes.addIconCircle}>+</span>
              Add New Row
            </Button>
          )}
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "16px",
            marginTop: "16px",
            marginLeft: "16px",
          }}
        >
          <Button
            className={classes.saveButton}
            type="button"
            onClick={handleSaveChanges}
            disabled={!isUpdated || !canEdit}
            style={{ opacity: !isUpdated || !canEdit ? 0.7 : 1, color: "#fff" }}
          >
            Save Selection
          </Button>
          <Button
            className={classes.resetChangesButton}
            type="button"
            onClick={handleResetChanges}
            disabled={!isUpdated || !canEdit}
            style={{ opacity: !isUpdated || !canEdit ? 0.8 : 1 }}
          >
            Reset Selection
          </Button>
        </div>
      </div>

      {errorMessage && (
        <div style={{ color: "red" }} className="error-message">
          {errorMessage}
        </div>
      )}
    </div>
  );
};

export default ReconciliationRunPerCmoTab;
